import { Controller } from "@hotwired/stimulus"

/**
 * Fills the adjacent input with a fresh UUID.
 *
 * Replaces an inline `onclick` that did
 * `document.getElementById('input-ai_insight[entity_id]')` — an id derived from
 * Ui::InputComponent's internal naming scheme, so any change to how that
 * component builds ids would have silently turned the button into a no-op. The
 * target declaration below is a contract the component honours through its
 * `data:` passthrough, not a guess at generated markup.
 *
 * It also removes an inline event handler from a page whose layout emits
 * `csp_meta_tag`; those are exactly what a script-src policy blocks.
 */
export default class extends Controller {
  static targets = ["input"]

  generate() {
    if (!this.hasInputTarget) return

    this.inputTarget.value = this.uuid()
    // Let anything listening (validation, dirty-tracking) see the change — a
    // direct `.value =` assignment fires no event on its own.
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  /**
   * `crypto.randomUUID` is unavailable on insecure origins in some browsers,
   * which includes plain-http staging hosts. Fall back to a v4 built from
   * getRandomValues rather than leaving the button dead there.
   */
  uuid() {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID()
    }

    const bytes = crypto.getRandomValues(new Uint8Array(16))
    bytes[6] = (bytes[6] & 0x0f) | 0x40 // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80 // variant 10xx
    const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("")
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
  }
}
